import React, { useMemo } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";
import useThemeColors from "./useThemeColors";

const BUCKETS = [
  { label: "0-15s", min: 0, max: 15 },
  { label: "15-30s", min: 15, max: 30 },
  { label: "30-60s", min: 30, max: 60 },
  { label: "1-3m", min: 60, max: 180 },
  { label: "3-10m", min: 180, max: 600 },
  { label: "10m+", min: 600, max: Infinity },
];

const CustomTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0]?.payload;
  if (!d) return null;
  return (
    <div className="bg-theme-secondary border border-theme-secondary rounded px-3 py-2 shadow-lg text-xs text-theme-text">
      <div className="font-medium">{d.label}</div>
      <div className="opacity-70 mt-0.5">{d.count} video{d.count !== 1 ? "s" : ""}</div>
    </div>
  );
};

const DurationDistribution = ({ videoRows }) => {
  const colors = useThemeColors();

  const { data, medianBucket } = useMemo(() => {
    const durations = videoRows
      .map((r) => r.video?.duration_seconds)
      .filter((d) => d != null && d > 0);

    const bucketCounts = BUCKETS.map((b) => ({
      ...b,
      count: durations.filter((d) => d >= b.min && d < b.max).length,
    }));

    // find bucket containing the median duration
    const sorted = [...durations].sort((a, b) => a - b);
    const med = sorted.length > 0 ? sorted[Math.floor(sorted.length / 2)] : null;
    const medIdx = med != null ? BUCKETS.findIndex((b) => med >= b.min && med < b.max) : -1;

    return { data: bucketCounts, medianBucket: medIdx };
  }, [videoRows]);

  const hasData = data.some((b) => b.count > 0);
  if (!hasData) {
    return (
      <div>
        <h4 className="text-sm font-semibold text-theme-text mb-2">Video Length</h4>
        <div className="flex items-center justify-center h-32 text-theme-text opacity-50 text-sm">
          No duration data available.
        </div>
      </div>
    );
  }

  return (
    <div>
      <h4 className="text-sm font-semibold text-theme-text mb-2">Video Length</h4>
      <ResponsiveContainer width="100%" height={160}>
        <BarChart data={data} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
          <XAxis
            dataKey="label"
            tick={{ fontSize: 11, fill: colors.text, fillOpacity: 0.5 }}
            stroke={colors.shadow}
          />
          <YAxis
            tick={{ fontSize: 11, fill: colors.text, fillOpacity: 0.5 }}
            stroke={colors.shadow}
            allowDecimals={false}
            width={28}
          />
          <Tooltip content={<CustomTooltip />} cursor={{ fill: colors.shadow, fillOpacity: 0.3 }} />
          <Bar dataKey="count" radius={[4, 4, 0, 0]} maxBarSize={36}>
            {data.map((_, i) => (
              <Cell
                key={i}
                fill={colors.primary}
                fillOpacity={i === medianBucket ? 0.85 : 0.4}
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default DurationDistribution;
